import {
    IonApp,
    IonRouterOutlet,
    setupIonicReact,
    useIonAlert,
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/float-elements.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/typography.css';
import { t } from '@lingui/macro';
import { useAtom } from 'jotai';
import React, { useEffect } from 'react';
import { Route, matchPath, useHistory, useLocation } from 'react-router-dom';

/* CSS for Swiper */
import 'swiper/css';
import 'swiper/css/pagination';

//import '@ionic/react/css/ionic-swiper.css';
import NotFound from './components/NotFound';
import NetworkStatus from './components/networkstatus/NetworkStatus';
import NocVersionInfo from './components/page/NocVersionInfo';
import GuardedRoute from './components/router/GuardedRoute';
import CeeEligibility from './pages/CeeEligibility';
import CeeQuote from './pages/CeeQuote';
import Installation from './pages/Installation';
import JobDetails from './pages/JobDetails';
import Jobs from './pages/Jobs';
import Login from './pages/Login';
import PastJobDetails from './pages/PastJobDetails';
import Uninstallation from './pages/Uninstallation';
import {
    jobIdAtom,
    jobsListResetAtom,
    onBeforeUnloadTimeAtom,
    prevInterventionPathnameAtom,
    resetInterventionAtom,
} from './store/Atoms';
import JotaiCallbacks from './store/JotaiCallbacks';
import './theme/styles.css';

/* Theme variables */
import './theme/variables.css';
import { useCustomRouter } from './utils/hooks/useCustomRouter';

setupIonicReact({
    mode: 'ios',
    scrollAssist: false,
    innerHTMLTemplatesEnabled: true,
});

const App: React.FC = () => {
    return (
        <IonApp>
            <NetworkStatus />
            <JotaiCallbacks />
            <IonReactRouter>
                <UpdateJobId />
                <IonRouterOutlet>
                    <Route exact path="/login" component={Login} />
                    <GuardedRoute exact path="/" component={Jobs} />
                    <GuardedRoute exact path="/past-jobs" component={Jobs} />
                    <GuardedRoute
                        exact
                        path="/:jobId(\d+)"
                        component={JobDetails}
                    />
                    <GuardedRoute
                        exact
                        path="/:jobId(\d+)/past-job"
                        component={PastJobDetails}
                    />
                    <GuardedRoute
                        exact
                        path="/:jobId(\d+)/cee-eligibility"
                        component={CeeEligibility}
                    />
                    <GuardedRoute
                        exact
                        path="/:jobId(\d+)/cee-quote"
                        component={CeeQuote}
                    />
                    <GuardedRoute
                        path="/:jobId(\d+)/installation"
                        component={Installation}
                    />
                    <GuardedRoute
                        path="/:jobId(\d+)/uninstallation"
                        component={Uninstallation}
                    />
                    <GuardedRoute
                        path="/noc_version_info"
                        component={NocVersionInfo}
                    />
                    <Route component={NotFound} />
                </IonRouterOutlet>
            </IonReactRouter>
        </IonApp>
    );
};
export default App;

const UpdateJobId: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const router = useCustomRouter();
    const [presentAlert] = useIonAlert();
    const [, resetIntervention] = useAtom(resetInterventionAtom);
    const [, resetJobsList] = useAtom(jobsListResetAtom);
    const [jobId, setJobId] = useAtom(jobIdAtom);
    const [prevInterventionPathname, setPrevInterventionPathname] = useAtom(
        prevInterventionPathnameAtom
    );
    const [onBeforeUnloadTime, setOnBeforeUnloadTime] = useAtom(
        onBeforeUnloadTimeAtom
    );

    useEffect(() => {
        const beforeUnloadListener = () => {
            const isInInstallation = !!matchPath<{ jobId?: string }>(
                location.pathname,
                {
                    path: '/:jobId/installation',
                    exact: false,
                    strict: false,
                }
            );
            const isInUninstallation = !!matchPath<{ jobId?: string }>(
                location.pathname,
                {
                    path: '/:jobId/uninstallation',
                    exact: false,
                    strict: false,
                }
            );

            if (isInInstallation || isInUninstallation) {
                setPrevInterventionPathname(location.pathname);
            } else {
                setPrevInterventionPathname('');
            }
            setOnBeforeUnloadTime(new Date().getTime());
        };

        window.addEventListener('beforeunload', beforeUnloadListener);

        return () => {
            window.removeEventListener('beforeunload', beforeUnloadListener);
        };
        // We are only interested in the pathname change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        const unblock = history.block((nextLocation) => {
            const isPrevInInstallation = !!matchPath<{ jobId?: string }>(
                location.pathname,
                {
                    path: '/:jobId/installation',
                    exact: false,
                    strict: false,
                }
            );
            const isPrevInUninstallation = !!matchPath<{ jobId?: string }>(
                location.pathname,
                {
                    path: '/:jobId/uninstallation',
                    exact: false,
                    strict: false,
                }
            );
            const isNextInIntervention = !!matchPath<{ jobId?: string }>(
                nextLocation.pathname,
                {
                    path: isPrevInInstallation
                        ? '/:jobId/installation'
                        : '/:jobId/uninstallation',
                    exact: false,
                    strict: false,
                }
            );
            const isNextLogin = !!matchPath<{ jobId?: string }>(
                nextLocation.pathname,
                {
                    path: '/login',
                    exact: false,
                    strict: false,
                }
            );
            const isCEEEligibilityPage = !!matchPath<{ jobId?: string }>(
                nextLocation.pathname,
                {
                    path: '/:jobId/cee-eligibility',
                    exact: false,
                    strict: false,
                }
            );

            if (
                !nextLocation.search.includes('interventionCompleted=true') &&
                (isPrevInInstallation || isPrevInUninstallation) &&
                !isNextInIntervention &&
                !isNextLogin &&
                !isCEEEligibilityPage
            ) {
                presentAlert({
                    header: t`Alert`,
                    message: t`Are you sure you want to cancel the intervention?`,
                    buttons: [
                        {
                            text: t`Cancel`,
                            role: 'cancel',
                        },
                        {
                            text: t`Ok`,
                            role: 'confirm',
                            handler: () => {
                                unblock();
                                router.push(nextLocation.pathname, 'back');
                                resetJobsList();
                            },
                        },
                    ],
                });
                return false;
            }

            if (nextLocation.search.includes('interventionCompleted=true')) {
                nextLocation.search = '';
                resetJobsList();
            }
        });
        return () => {
            unblock();
        };
    }, [
        history,
        location.pathname,
        presentAlert,
        resetJobsList,
        resetIntervention,
        router,
    ]);

    useEffect(() => {
        const prevMatch = matchPath<{ jobId?: string }>(
            prevInterventionPathname,
            {
                path: '/:jobId',
                exact: false,
                strict: false,
            }
        );
        const match = matchPath<{ jobId?: string }>(location.pathname, {
            path: '/:jobId',
            exact: false,
            strict: false,
        });

        const prevJobId = prevMatch?.params?.jobId
            ? +prevMatch.params.jobId
            : 0;
        const newJobId = match?.params?.jobId ? +match.params.jobId : 0;
        const seconds = 5 * 1000;
        const now = new Date().getTime();
        const timeDiff = now - onBeforeUnloadTime;

        if (
            timeDiff < seconds &&
            newJobId &&
            prevJobId &&
            newJobId !== prevJobId
        ) {
            presentAlert({
                header: t`Alert`,
                message: t`You switched to another intervention. If this is intentional, please click 'Ok' to continue or 'Cancel' to go back to the previous intervention.`,
                buttons: [
                    {
                        text: t`Cancel`,
                        role: 'confirm',
                        handler: () => {
                            router.push(
                                prevInterventionPathname,
                                'none',
                                'replace'
                            );
                        },
                    },
                    {
                        text: t`Ok`,
                        role: 'confirm',
                    },
                ],
            });
        }

        if (newJobId && newJobId !== jobId) {
            // We are going to a new job id so we need to reset the intervention
            resetIntervention();
            setJobId(newJobId);
        }

        setPrevInterventionPathname('');
        setOnBeforeUnloadTime(0);
        // We are only interested in the pathname change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return null;
};
